import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DollarSign, Lock, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { trackEvent } from '@/hooks/useAnalytics';

/* ═══ Login-specific copy — EN & ES ═══ */
const COPY = {
  en: {
    platformLabel: 'Brand Intelligence Platform',
    accessTitle: 'Secure Access',
    emailLabel: 'Email',
    emailPlaceholder: 'you@organization.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter password',
    signIn: 'Sign In',
    signingIn: 'Signing in\u2026',
    accessNote: 'Access is restricted to authorised users. Contact your administrator for credentials.',
    backToSite: 'Back to website',
    confidential: 'YBV Brand Intelligence Platform \u2014 Confidential',
  },
  es: {
    platformLabel: 'Plataforma de Inteligencia de Marca',
    accessTitle: 'Acceso Seguro',
    emailLabel: 'Correo electr\u00f3nico',
    emailPlaceholder: 'tu@organizacion.com',
    passwordLabel: 'Contrase\u00f1a',
    passwordPlaceholder: 'Introducir contrase\u00f1a',
    signIn: 'Iniciar Sesi\u00f3n',
    signingIn: 'Iniciando sesi\u00f3n\u2026',
    accessNote: 'Acceso restringido a usuarios autorizados. Contacta con tu administrador para obtener credenciales.',
    backToSite: 'Volver al sitio web',
    confidential: 'YBV Plataforma de Inteligencia de Marca \u2014 Confidencial',
  },
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/us-soccer';

  const c = COPY[lang] || COPY.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      trackEvent('login_success');
      // Redirect to the referrer, or to the user's first tenant portal
      const firstTenant = (data?.user?.tenant_access || [])[0]?.tenant_id || 'us-soccer';
      const dest = from !== '/login' ? from : `/${firstTenant}`;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-10">

        {/* ── Brand ── */}
        <div className="flex flex-col items-center gap-3">
          <Link to="/" className="flex flex-col items-center gap-3 hover:opacity-80 transition-opacity" data-testid="login-logo">
            <div className="w-12 h-12 rounded-xl bg-stable/10 border border-stable/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-stable" />
            </div>
            <div className="text-center">
              <h1 className="text-lg font-semibold text-foreground tracking-tight">YBV</h1>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">{c.platformLabel}</p>
            </div>
          </Link>
        </div>

        {/* ── Login Card ── */}
        <Card className="bg-card border-border/50">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Lock className="w-4 h-4" />
              <span className="font-medium">{c.accessTitle}</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="login-email-input" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {c.emailLabel}
                </label>
                <Input
                  id="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={c.emailPlaceholder}
                  required
                  className="bg-background border-border/50 h-10"
                  data-testid="login-email"
                  autoComplete="email"
                  aria-label={c.emailLabel}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="login-password-input" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {c.passwordLabel}
                </label>
                <div className="relative">
                  <Input
                    id="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={c.passwordPlaceholder}
                    required
                    className="bg-background border-border/50 h-10 pr-10"
                    data-testid="login-password"
                    autoComplete="current-password"
                    aria-label={c.passwordLabel}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-destructive" role="alert" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 h-11 text-[15px] font-semibold"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? c.signingIn : c.signIn}
              </Button>
            </form>

            <p className="text-2xs text-center text-muted-foreground/50">
              {c.accessNote}
            </p>
          </CardContent>
        </Card>

        {/* ── Footer ── */}
        <div className="flex items-center justify-center gap-4 text-2xs text-muted-foreground/40">
          <Link to="/" className="flex items-center gap-1 text-muted-foreground/50 hover:text-muted-foreground transition-colors" data-testid="login-back-to-site">
            <ArrowLeft className="w-3 h-3" />
            {c.backToSite}
          </Link>
          <span>&middot;</span>
          <span>{c.confidential}</span>
        </div>

      </div>
    </div>
  );
}
